import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Article {
  id: number;
  title: string;
  summary: string;
  content: string;
  image: string | null;
  status: string;
  category: string;
  cultivation_stage: string;
  tags: string[];
  keywords: string[];
  is_featured: number;
  author_name?: string;
  created_at: string;
}

const Articles: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadArticles = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('https://fellfell.ir/api/articles.php', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
        setArticles(data.data);
      } else {
        setError('خطا در دریافت مقالات: ساختار داده نامعتبر');
      }
    } catch (err: any) {
      setError('خطا در ارتباط با سرور: ' + err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadArticles();
  }, []);

  const handleDelete = async (id: number) => {
    if (!confirm('آیا از حذف این مقاله مطمئن هستید؟')) return;
    try {
      const response = await fetch(`https://fellfell.ir/api/articles.php?id=${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        showToast('✅ مقاله با موفقیت حذف شد', 'success');
        loadArticles();
      } else {
        showToast('❌ خطا در حذف مقاله: ' + (data.message || 'خطای نامشخص'), 'error');
      }
    } catch (err: any) {
      showToast('❌ خطا در حذف مقاله: ' + err.message, 'error');
    }
  };

  const handleStatusChange = async (id: number, status: string) => {
    try {
      const getResponse = await fetch(`https://fellfell.ir/api/articles.php?action=single&id=${id}`, {
        credentials: 'include'
      });
      const getData = await getResponse.json();
      
      if (!getData.success || !getData.data) {
        showToast('❌ خطا در دریافت اطلاعات مقاله', 'error');
        return;
      }
      
      const article = getData.data;
      
      const response = await fetch(`https://fellfell.ir/api/articles.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: article.title,
          summary: article.summary || '',
          content: article.content,
          status: status,
          category: article.category || '',
          cultivation_stage: article.cultivation_stage || 'کشت',
          tags: article.tags || [],
          keywords: article.keywords || [],
          is_featured: article.is_featured || 0,
          image: article.image || null,
          author_name: article.author_name || ''
        })
      });
      
      const data = await response.json();
      if (data.success) {
        showToast(`✅ وضعیت مقاله به ${status === 'published' ? 'منتشر شده' : 'پیش‌نویس'} تغییر یافت`, 'success');
        loadArticles();
      } else {
        showToast('❌ خطا در تغییر وضعیت: ' + (data.message || 'خطای نامشخص'), 'error');
      }
    } catch (err: any) {
      showToast('❌ خطا در تغییر وضعیت: ' + err.message, 'error');
    }
  };

  const getStageBadge = (stage: string) => {
    const stages: Record<string, { label: string; color: string; bg: string; icon: string }> = {
      'کشت': { label: 'کشت', color: '#2e7d32', bg: '#e8f5e9', icon: '🌱' },
      'داشت': { label: 'داشت', color: '#1565c0', bg: '#e3f2fd', icon: '🌿' },
      'برداشت': { label: 'برداشت', color: '#f57c00', bg: '#fff3e0', icon: '🌾' },
      'نگهداشت': { label: 'نگهداشت', color: '#6a1b9a', bg: '#f3e5f5', icon: '🏺' }
    };
    return stages[stage] || { label: stage, color: '#333', bg: '#f5f5f5', icon: '📌' };
  };

  if (loading) {
    return (
      <div style={{ direction: 'rtl', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', fontSize: '18px', color: '#555' }}>
        ⏳ در حال بارگذاری مقالات...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ direction: 'rtl', padding: '20px' }}>
        <div style={{ background: '#ffebee', color: '#c62828', padding: '15px 20px', borderRadius: '10px', borderRight: '4px solid #c62828' }}>
          ❌ {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ direction: 'rtl', padding: '20px' }}>
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '14px 28px',
          borderRadius: '12px',
          fontSize: '15px',
          zIndex: 9999,
          background: toast.type === 'success' ? '#2e7d32' : '#c62828',
          color: '#fff',
          boxShadow: '0 6px 25px rgba(0,0,0,0.25)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h1 style={{ color: '#1e3a1e', fontSize: '24px', margin: 0 }}>✍️ مدیریت مقالات</h1>
          <p style={{ color: '#888', fontSize: '14px', margin: '5px 0 0' }}>
            {articles.length} مقاله در سیستم وجود دارد
          </p>
        </div>
        <button 
          onClick={() => navigate('/admin/article/add')}
          style={{
            padding: '10px 24px',
            background: '#2e7d32',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#1b5e20'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#2e7d32'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          ✍️ افزودن مقاله جدید
        </button>
      </div>

      {articles.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#fff', borderRadius: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>📝</div>
          <p style={{ fontSize: '18px', color: '#888' }}>هیچ مقاله‌ای یافت نشد</p>
          <button onClick={() => navigate('/admin/article/add')} style={{ marginTop: '20px', padding: '10px 30px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
            ✍️ افزودن اولین مقاله
          </button>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
              <thead>
                <tr style={{ background: '#f5faf5' }}>
                  <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>تصویر</th>
                  <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>عنوان</th>
                  <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>نویسنده</th>
                  <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>مرحله کشت</th>
                  <th style={{ padding: '12px 15px', textAlign: 'right', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>وضعیت</th>
                  <th style={{ padding: '12px 15px', textAlign: 'center', borderBottom: '2px solid #e8f0e8', fontSize: '13px', color: '#555' }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((article) => {
                  const imagePath = article.image 
                    ? `/uploads/articles/${article.image}`
                    : '/images/default-article.jpg';
                  const stage = getStageBadge(article.cultivation_stage || 'کشت');
                  
                  return (
                    <tr key={article.id} style={{ borderBottom: '1px solid #eef5ee' }}>
                      <td style={{ padding: '12px 15px' }}>
                        <img 
                          src={imagePath}
                          alt={article.title}
                          style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e8f0e8' }}
                          onError={(e) => { (e.target as HTMLImageElement).src = '/images/default-article.jpg'; }}
                          loading="lazy"
                          decoding="async"
                        />
                      </td>
                      <td style={{ padding: '12px 15px', fontWeight: '500' }}>
                        {article.title}
                        {article.is_featured === 1 && (
                          <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '11px', background: '#fff3e0', color: '#e65100', marginRight: '5px' }}>⭐ ویژه</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 15px', color: '#666', fontSize: '13px' }}>
                        {article.author_name || 'ناشناس'}
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          background: stage.bg,
                          color: stage.color,
                          fontWeight: '600'
                        }}>
                          {stage.icon} {stage.label}
                        </span>
                      </td>
                      <td style={{ padding: '12px 15px' }}>
                        <span 
                          style={{
                            padding: '3px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            background: article.status === 'published' ? '#e8f5e9' : '#ffebee',
                            color: article.status === 'published' ? '#2e7d32' : '#c62828',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleStatusChange(article.id, article.status === 'published' ? 'draft' : 'published')}
                        >
                          {article.status === 'published' ? '✅ منتشر شده' : '⛔ پیش‌نویس'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button 
                            onClick={() => navigate(`/admin/article/edit/${article.id}`)}
                            style={{ padding: '6px 12px', background: '#e3f2fd', color: '#0d47a1', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                            title="ویرایش مقاله"
                          >
                            ✏️
                          </button>
                          <button 
                            onClick={() => handleDelete(article.id)}
                            style={{ padding: '6px 12px', background: '#ffebee', color: '#c62828', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}
                            title="حذف مقاله"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Articles;